import { __decorate } from "tslib";
import { customApiPrefix } from "./../../core/templates/apis/custom";
import { migrateDataSchema } from '@tencent/data-schema-core';
import { StateShallow, Vue } from "../../vue";
import { State, Getter } from 'vue-class-state';
import { Inject } from 'vue-property-decorator';
import { cloneDeep, debounce } from "lodash-es";
import { getMonacoAsync } from "../../tools/monacoProvider";
import { message, Modal } from 'ant-design-vue';
import { debugMethod, defineMethod } from "../../core/stateJS/utils/defineMethod";
import { generateMethod } from "../..";
import { arrayRemove } from "../../tools/lang";
import { $methodKey } from "../../core/stateJS/utils/consts";
import { delete$MethodsSchema } from "../../core/stateJS/utils/set$MethodSchema";
import { EDITOR_TAB, monacoInitOptions } from "./consts";
import { LogState } from "./LogState";
import { getTypesStringBySchema } from "./codeTypes";
import { getMethodArgSchema } from "../../core/stateJS/utils/getMethodArgsSchemas";
import { getSandboxFunc } from "../../tools/getSandboxFunction";
import { METHOD_TYPES } from "../../core/stateCommon/consts";
import { getMethodGeneratorByModel } from "../../core/stateJS/methods";
import { triggerSourceUpdate } from "../../tools/designer/triggerSourceUpdate";
import { getDefaultSchemaProcess, getSchemaProcesses } from "./variablesSchema";
import { reportClick } from "../../tools/report";
import { LazyGetter } from "../../tools/decorator/LazyGetter";
import { getMessageSpy } from "./getMessageSpy";
import { NotEnumerable } from "../../tools/enumerable";
import styles from "./components/style.module.css";
import { disposeWatchers } from "../../tools/disposeWatchers";
/**
 * 数据源方法代码编辑器状态管理
 */
export class MethodEditorState {
  constructor() {
    this.logState = new LogState(this);
    this.currentTab = [EDITOR_TAB.debug];
    this.watcher = new Vue();
    this.lastWatcherDispose = null;
    this.mode = 'edit';
    this.visible = false;
    this.sourceId = '';
    /**
     * 原始的方法名，因为方法名可以改
     */
    this.originMethodId = '';
    this.generators = [];
    this.selectedTemplateValue = '';
    this.method = null;
    this.modified = false;
    this.hasError = false;
    this.highlightDataPath = '';
    this.highlightKeyForDefaultValue = '';
    this.codeEditorReady = new Promise(r => this.codeEditorResolve = r);
    this.firstFormat = true;
    this.checkFunctionError = debounce(this.checkFunctionError, 1000);
  }
  get sourceConfig() {
    return this.getSourceConfig(this.sourceId);
  }
  get sourceInstance() {
    return this.getSourceInstance(this.sourceId);
  }
  get methodTemplates() {
    const list = this.generators.map(item => {
      var _item$cgi;
      const text = (_item$cgi = item.cgi) !== null && _item$cgi !== void 0 && _item$cgi.startsWith(customApiPrefix) ? `调用自定义接口(${item.cgi})` : item.title;
      return {
        valueObject: item,
        value: `${item.tpl}_${item.cgi || 'null'}`,
        label: text
      };
    });
    return list;
  }
  get selectedGenerator() {
    var _this$methodTemplates;
    return (_this$methodTemplates = this.methodTemplates.find(item => item.value === this.selectedTemplateValue)) === null || _this$methodTemplates === void 0 ? void 0 : _this$methodTemplates.valueObject;
  }
  get canClose() {
    return !(this.visible && this.modified);
  }
  get isCreateMode() {
    return this.mode === 'create';
  }
  get title() {
    return this.isCreateMode ? '创建方法' : '编辑方法';
  }
  get designer() {
    return this.options.designer;
    // return this.options.pageConfig.$refs.uicoreDesigner;
  }
  get collection() {
    return this.dataStub.value.collection.collectionData;
  }
  get stateModel() {
    var _this$dataStub$value;
    return (_this$dataStub$value = this.dataStub.value) === null || _this$dataStub$value === void 0 ? void 0 : _this$dataStub$value.stateModel;
  }
  get methodType() {
    var _this$method;
    return (_this$method = this.method) === null || _this$method === void 0 ? void 0 : _this$method.type;
  }
  get isForm() {
    return this.methodType === METHOD_TYPES.form;
  }
  get isCode() {
    return this.methodType === METHOD_TYPES.code;
  }
  get schemaProcesses() {
    var _this$sourceConfig;
    const stateModel = (_this$sourceConfig = this.sourceConfig) === null || _this$sourceConfig === void 0 ? void 0 : _this$sourceConfig.stateModel;
    if (!stateModel) return [];
    return getSchemaProcesses(stateModel);
  }
  get schemaProcess() {
    var _this$sourceConfig2;
    const stateModel = (_this$sourceConfig2 = this.sourceConfig) === null || _this$sourceConfig2 === void 0 ? void 0 : _this$sourceConfig2.stateModel;
    if (!stateModel) return null;
    return getDefaultSchemaProcess(stateModel);
  }
  get canResetMethod() {
    var _this$method2;
    return !this.isCreateMode && Boolean((_this$method2 = this.method) === null || _this$method2 === void 0 || (_this$method2 = _this$method2.meta) === null || _this$method2 === void 0 ? void 0 : _this$method2.tpl);
  }
  reset() {
    var _this$codeEditor, _this$debugEditor, _this$lastWatcherDisp;
    this.sourceId = '';
    this.originMethodId = '';
    this.method = null;
    this.modified = false;
    (_this$codeEditor = this.codeEditor) === null || _this$codeEditor === void 0 || _this$codeEditor.setValue('');
    (_this$debugEditor = this.debugEditor) === null || _this$debugEditor === void 0 || _this$debugEditor.setValue('');
    (_this$lastWatcherDisp = this.lastWatcherDispose) === null || _this$lastWatcherDisp === void 0 || _this$lastWatcherDisp.call(this);
    this.highlightKeyForDefaultValue = '';
  }
  async initCreateMode(options) {
    // 如果前面有未保存的editmode，需要提示 return
    if (this.visible && !this.isCreateMode && this.modified) {
      message.warn('有未保存的修改,请先保存或退出后再添加方法');
      return;
    }
    this.dataStub = options.dataStub;
    this.reset();
    this.mode = 'create';
    this.show();
    const {
      collection
    } = this;
    const sourceConfig = this.getSourceConfig(options.sourceId);
    const generators = getMethodGeneratorByModel({
      collection,
      stateModel: this.stateModel,
      usage: sourceConfig === null || sourceConfig === void 0 ? void 0 : sourceConfig.usage,
      needEmpty: true
    });
    this.generators = generators;
    // 默认使用第一个cgi作为method
    const first = this.methodTemplates[0];
    // 写个假的 避免高度问题
    this.method = {
      id: '',
      title: '',
      code: '',
      debugCode: '',
      autoCatch: true,
      loading: true,
      type: METHOD_TYPES.code
    };
    if (first) {
      await this.setCreateModeSelectedTpl(first.value);
    } else {
      // todo
    }
    this.sourceId = options.sourceId;
    this.originMethodId = '';
  }
  getMethodGenerateOptions(tpl, cgi, methodType) {
    const {
      collection,
      stateModel
    } = this;
    const extraConfig = this.options.pageConfig.wContext.projectConfig.dataSourceConfig;
    return {
      cgi,
      tpl,
      collection,
      stateModel,
      extraConfig,
      methodType
    };
  }
  async setCreateModeSelectedTpl(tpl) {
    this.selectedTemplateValue = tpl;
    if (!this.selectedGenerator) {
      throw new Error('selectedGenerator is null');
    }
    const options = this.getMethodGenerateOptions(this.selectedGenerator.tpl, this.selectedGenerator.cgi);
    const method = await generateMethod(options);
    this.method = method;
    await this.setValueAndFormat(method);
    this.watchMethod(this.method);
  }
  // eslint-disable-next-line complexity
  async initEditMode(options) {
    if (this.visible && this.isCreateMode) {
      message.warn('请先保存或退出后再切换到编辑');
      return;
    }
    if (this.visible && this.modified && (this.sourceId !== options.sourceId || this.originMethodId !== options.methodId)) {
      message.warn('请先保存或退出后再切换到其他方法');
      return;
    }
    this.dataStub = options.dataStub;
    this.mode = 'edit';
    const {
      sourceId,
      methodId
    } = options;
    this.highlightKeyForDefaultValue = options.highlightDataKey || '';
    if (this.sourceId === sourceId && this.originMethodId === methodId) {
      this.show();
      return;
    }
    const method = this.getMethodConfig(sourceId, methodId);
    if (method) {
      this.sourceId = sourceId;
      this.originMethodId = methodId;
      const newMethod = cloneDeep(method);
      this.method = newMethod;
      this.logState.clear();
      this.show();
      await this.setValueAndFormat(method);
      this.modified = false;
      this.watchMethod(newMethod);
    } else {
      message.error(`${sourceId} ${methodId} not found`);
    }
    this.highlightWord(this.highlightKeyForDefaultValue);
  }
  /**
   * 高亮某个字符串代码
   * @param word
   * @returns
   */
  async highlightWord(word) {
    var _this$method3;
    if (!(word.trim() && ((_this$method3 = this.method) === null || _this$method3 === void 0 ? void 0 : _this$method3.id) === 'init')) {
      return;
    }
    await this.codeEditorReady;
    try {
      var _this$codeEditor$getM;
      const highlightString = `source.${word}`;
      // @ts-ignore
      const range = (_this$codeEditor$getM = this.codeEditor.getModel()) === null || _this$codeEditor$getM === void 0 || (_this$codeEditor$getM = _this$codeEditor$getM.findMatches(highlightString)) === null || _this$codeEditor$getM === void 0 || (_this$codeEditor$getM = _this$codeEditor$getM[0]) === null || _this$codeEditor$getM === void 0 ? void 0 : _this$codeEditor$getM.range;
      // this.codeEditor.setSelection(range);
      // this.codeEditor.getAction('actions.findWithSelection').run();
      if (!range) {
        return;
      }
      this.codeEditor.deltaDecorations([], [{
        range,
        options: {
          inlineClassName: styles.highlightForCode
        }
      }]);
    } catch (error) {
      console.warn(error);
    }
  }
  watchMethod(newMethod) {
    var _this$lastWatcherDisp2;
    (_this$lastWatcherDisp2 = this.lastWatcherDispose) === null || _this$lastWatcherDisp2 === void 0 || _this$lastWatcherDisp2.call(this);
    this.lastWatcherDispose = this.watcher.$watch(() => newMethod, () => {
      this.modified = true;
      if (this.method) {
        this.method.unchanged = false;
      }
    }, {
      deep: true
    });
  }
  getMethodConfig(sourceId, methodId) {
    var _source$methods;
    const source = this.getSourceConfig(sourceId);
    return (source === null || source === void 0 || (_source$methods = source.methods) === null || _source$methods === void 0 ? void 0 : _source$methods.find(item => item.id === methodId)) || null;
  }
  getSourceConfig(sourceId) {
    var _this$designer$rootPa;
    return ((_this$designer$rootPa = this.designer.rootPagelet) === null || _this$designer$rootPa === void 0 || (_this$designer$rootPa = _this$designer$rootPa.xySources) === null || _this$designer$rootPa === void 0 ? void 0 : _this$designer$rootPa.find(item => item.id === sourceId)) || null;
  }
  async setEditorSuggestion() {
    try {
      var _this$typesDisposer, _this$designer$render;
      await this.codeEditorReady;
      (_this$typesDisposer = this.typesDisposer) === null || _this$typesDisposer === void 0 || _this$typesDisposer.dispose();
      const monaco = await getMonacoAsync();
      // https://microsoft.github.io/monaco-editor/playground.html#extending-language-services-configure-javascript-defaults
      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ESNext,
        allowNonTsExtensions: true,
        allowJs: true
        // strict: false,
      });
      // extra libraries
      const libSource = await getTypesStringBySchema((_this$designer$render = this.designer.renderer) === null || _this$designer$render === void 0 ? void 0 : _this$designer$render.ucDataSources, this.dataStub);
      if (!libSource) return;
      const libUri = 'wuji.d.ts';
      this.typesDisposer = monaco.languages.typescript.javascriptDefaults.addExtraLib(libSource, libUri);
      // When resolving definitions and references, the editor will try to use created models.
      // Creating a model for the library allows "peek definition/references" commands to work with the library.
      // monaco.editor.createModel(libSource, 'typescript', monaco.Uri.parse(libUri));
    } catch (error) {
      console.error(error);
    }
  }
  async addEditorActions(editor) {
    const monaco = await getMonacoAsync();
    const KC = monaco.KeyCode;
    editor.addAction({
      id: 'wuji_method_save',
      label: '保存',
      keybindings: [KC.F4],
      contextMenuGroupId: 'navigation',
      run: () => {
        this.onSave();
      }
    });
    editor.addAction({
      id: 'wuji_method_debug',
      label: '保存并调试',
      keybindings: [KC.F5],
      contextMenuGroupId: 'navigation',
      run: () => {
        this.debug();
      }
    });
    editor.addAction({
      id: 'wuji_method_suggest',
      label: '触发智能提示',
      keybindings: [KC.F6],
      contextMenuGroupId: 'navigation',
      run: () => {
        editor.getAction('editor.action.triggerSuggest').run();
      }
    });
  }
  async initCodeEditor(el) {
    var _this$method$code, _this$method4;
    if (this.codeEditor) return;
    const monaco = await getMonacoAsync();
    this.codeEditor = monaco.editor.create(el, {
      ...monacoInitOptions
    });
    this.codeEditor.onDidChangeModelContent(_e => {
      if (this.method) {
        this.method.code = this.codeEditor.getValue();
        this.checkFunctionError();
      }
    });
    await this.addEditorActions(this.codeEditor);
    const uri = monaco.Uri.parse('file:///data-source-method.js');
    let model = monaco.editor.getModel(uri);
    if (!model) model = monaco.editor.createModel((_this$method$code = (_this$method4 = this.method) === null || _this$method4 === void 0 ? void 0 : _this$method4.code) !== null && _this$method$code !== void 0 ? _this$method$code : '', 'javascript', uri);
    this.codeEditor.setModel(model);
    this.codeModel = model;
    this.codeEditorResolve(this.codeEditor);
  }
  updateArgs() {
    if (!this.method) return;
    try {
      const res = getMethodArgSchema(this.method.code);
      // console.log('参数列表', res);
      if (this.method.args) {
        const container = {
          id: 'args',
          type: 'object'
        };
        // migrateDataSchema不支持数组，这里中转下
        const old = {
          ...container,
          fields: cloneDeep(this.method.args || [])
        };
        const newArgs = migrateDataSchema(old, {
          ...container,
          fields: res || []
        });
        // console.log('新的参数', newArgs);
        Vue.set(this.method, 'args', newArgs.fields);
      } else {
        Vue.set(this.method, 'args', res);
      }
    } catch (error) {
      console.error(error);
    }
  }
  async onSave(execute = false) {
    var _this$lastWatcherDisp3;
    const isOk = await this.validateFrom();
    if (!isOk) {
      return false;
    }
    (_this$lastWatcherDisp3 = this.lastWatcherDispose) === null || _this$lastWatcherDisp3 === void 0 || _this$lastWatcherDisp3.call(this); // 避免内部改动被收集到
    await this.formatCode();
    const value = this.codeEditor.getValue();
    this.method.code = value;
    this.updateArgs();
    const newMethod = cloneDeep(this.method);
    const source = this.getSourceConfig(this.sourceId);
    if (!source) {
      throw new Error(`${this.sourceId}的配置找不到`);
    }
    // 更新 数据源pagelet的config
    if (this.isCreateMode) {
      // todo 检查重复id
      source === null || source === void 0 || source.methods.push(newMethod);
    } else {
      const index = source === null || source === void 0 ? void 0 : source.methods.findIndex(item => item.id === this.originMethodId);
      if (typeof index === 'number' && index >= 0) {
        source === null || source === void 0 || source.methods.splice(index, 1, newMethod);
      } else {
        message.error(`找不到${this.sourceId}.$methods.${this.originMethodId}方法的配置`);
        return false;
      }
    }
    // 重新定义运行时的function
    const sourceInstance = this.getSourceInstance(this.sourceId);
    if (sourceInstance) {
      defineMethod(sourceInstance, newMethod, {
        originMethodId: this.originMethodId
      });
    }
    this.triggerDesignerUpdate();
    this.modified = false;
    this.originMethodId = newMethod.id;
    if (this.isCreateMode) {
      this.mode = 'edit';
      this.method = newMethod;
    }
    this.watchMethod(this.method);
    // 这里需要保存数据库吗
    message.success(`data.${this.sourceId}.$methods.${newMethod.id} 已保存成功`);
    if (execute) {
      sourceInstance.$methods[newMethod.id]();
      message.info(`data.${this.sourceId}.$methods.${newMethod.id} 已触发执行`);
    }
    return true;
  }
  triggerDesignerUpdate() {
    triggerSourceUpdate(this.designer, this.sourceId);
  }
  async onSaveAndExit(execute) {
    const isSucc = await this.onSave(execute);
    if (isSucc) {
      this.hide();
    }
  }
  onDelete() {
    const {
      originMethodId,
      sourceId
    } = this;
    this.deleteMethod(originMethodId, sourceId);
  }
  deleteMethod(methodId, sourceId) {
    Modal.confirm({
      content: '确定要删除该方法吗',
      okText: '删除',
      onOk: () => {
        const source = this.getSourceConfig(sourceId);
        if (source) {
          const method = source.methods.find(item => item.id === methodId);
          arrayRemove(source.methods, method);
          const instance = this.getSourceInstance(sourceId);
          if (instance) {
            delete instance[$methodKey][methodId];
            delete$MethodsSchema(instance, methodId);
          }
          triggerSourceUpdate(this.designer, sourceId);
          this.hide();
          this.reset();
        } else {
          message.error(`找不到 [${sourceId}] 数据源配置`);
        }
        reportClick({
          eid: '删除方法',
          mod: '数据源方法'
        });
      }
    });
  }
  onClose() {
    if (!this.modified) {
      this.hide();
      return;
    }
    Modal.confirm({
      content: '直接退出，你的更改将丢失',
      title: '该方法已有变更，是否直接退出',
      onOk: () => {
        this.modified = false;
        this.hide();
      },
      okText: '直接退出',
      cancelText: '取消'
    });
  }
  getSourceInstance(sourceId) {
    return this.designer.data[sourceId] || null;
  }
  async formatCode() {
    await new Promise(r => setTimeout(r, 10));
    await this.codeEditor.getAction('editor.action.formatDocument').run();
    // await this.codeEditor.trigger(null, 'editor.action.formatDocument', null);
  }
  async setValueAndFormat(method) {
    var _this$debugEditor2;
    await this.codeEditorReady;
    this.codeEditor.setValue(method.code);
    (_this$debugEditor2 = this.debugEditor) === null || _this$debugEditor2 === void 0 || _this$debugEditor2.setValue(method.debugCode);
    this.codeEditor.setScrollTop(0, 0);
    // await this.formatCode();
  }
  checkMethodName(methodName, exclude) {
    if (exclude && methodName === exclude) {
      return true;
    }
    const source = this.getSourceConfig(this.sourceId);
    if (!source) {
      return true;
    }
    return source.methods.every(item => item.id !== methodName);
  }
  validateFrom() {
    return new Promise(r => {
      var _this$$info;
      (_this$$info = this.$info) === null || _this$$info === void 0 || _this$$info.form.validate(isOk => {
        r(isOk);
      });
    });
  }
  dispose() {
    var _this$codeEditor2, _this$codeModel, _this$typesDisposer2;
    this.watcher.$destroy();
    (_this$codeEditor2 = this.codeEditor) === null || _this$codeEditor2 === void 0 || _this$codeEditor2.dispose();
    (_this$codeModel = this.codeModel) === null || _this$codeModel === void 0 || _this$codeModel.dispose();
    (_this$typesDisposer2 = this.typesDisposer) === null || _this$typesDisposer2 === void 0 || _this$typesDisposer2.dispose();
    disposeWatchers(this);
  }
  show() {
    var _this$codeEditor3, _this$debugEditor3;
    this.visible = true;
    this.setEditorSuggestion();
    // updateOptions 并没有什么卵用
    (_this$codeEditor3 = this.codeEditor) === null || _this$codeEditor3 === void 0 || _this$codeEditor3.updateOptions({
      theme: 'vs-dark'
    });
    (_this$debugEditor3 = this.debugEditor) === null || _this$debugEditor3 === void 0 || _this$debugEditor3.updateOptions({
      theme: 'vs-dark'
    });
  }
  hide() {
    var _this$typesDisposer3;
    this.visible = false;
    (_this$typesDisposer3 = this.typesDisposer) === null || _this$typesDisposer3 === void 0 || _this$typesDisposer3.dispose();
    this.reset();
  }
  async initDebugEditor(el) {
    var _this$method$debugCod, _this$method5;
    if (this.debugEditor) {
      return;
    }
    const monaco = await getMonacoAsync();
    this.debugEditor = monaco.editor.create(el, {
      value: (_this$method$debugCod = (_this$method5 = this.method) === null || _this$method5 === void 0 ? void 0 : _this$method5.debugCode) !== null && _this$method$debugCod !== void 0 ? _this$method$debugCod : '',
      language: 'javascript',
      ...monacoInitOptions,
      lineNumbers: 'off'
    });
    this.debugEditor.onDidChangeModelContent(() => {
      if (!this.method) {
        return;
      }
      this.method.debugCode = this.debugEditor.getValue();
    });
    await this.addEditorActions(this.debugEditor);
  }
  async debug() {
    const isOk = await this.onSave(false);
    if (!isOk) return;
    const sourceInstance = this.getSourceInstance(this.sourceId);
    if (!sourceInstance || !this.method) {
      return;
    }
    this.logState.clear();
    await new Promise(r => setTimeout(r, 128));
    this.logState.push({
      level: 'log',
      content: '调试开始'
    });
    try {
      const console = this.logState.consoleSpy;
      await debugMethod(sourceInstance, this.method, {
        console
      });
    } catch (error) {
      console.error(error);
      message.error(error.message);
    }
  }
  checkFunctionError() {
    var _this$method6;
    if (!((_this$method6 = this.method) !== null && _this$method6 !== void 0 && _this$method6.code)) {
      return;
    }
    try {
      getSandboxFunc(this.method.code);
      this.hasError = false;
    } catch (error) {
      this.hasError = true;
      // console.error(error);
    }
  }
  get convertible() {
    var _this$method7, _this$method8;
    const meta = (_this$method7 = this.method) === null || _this$method7 === void 0 ? void 0 : _this$method7.meta;
    if (typeof (meta === null || meta === void 0 ? void 0 : meta.convertible) === 'boolean') {
      return meta.convertible;
    }
    return Boolean((_this$method8 = this.method) === null || _this$method8 === void 0 || (_this$method8 = _this$method8.meta) === null || _this$method8 === void 0 ? void 0 : _this$method8.tpl);
  }
  /**
   * 切换到可视化
   */
  async transformToForm() {
    if (!this.convertible) return;
    try {
      var _this$method9, _this$method$meta;
      if ((_this$method9 = this.method) !== null && _this$method9 !== void 0 && _this$method9.formInfo) {
        return;
      }
      const options = this.getMethodGenerateOptions(this.method.meta.tpl, (_this$method$meta = this.method.meta) === null || _this$method$meta === void 0 ? void 0 : _this$method$meta.cgi, METHOD_TYPES.form);
      const newMethod = await generateMethod(options);
      Vue.set(this.method, 'formInfo', newMethod.formInfo);
    } catch (error) {
      throw error;
    } finally {
      this.method.type = METHOD_TYPES.form;
    }
  }
  /**
   * 切换到代码
   */
  async transformToCode() {
    if (!this.convertible) return;
    try {
      var _this$method10;
      if ((((_this$method10 = this.method) === null || _this$method10 === void 0 ? void 0 : _this$method10.code.length) || 0) > 1) {
        return;
      }
      const options = this.getMethodGenerateOptions(this.method.meta.tpl, this.method.meta.cgi, METHOD_TYPES.code);
      const newMethod = await generateMethod(options);
      this.method.code = newMethod.code;
      this.method.args = newMethod.args;
      this.setValueAndFormat(this.method);
    } catch (error) {
      throw error;
    } finally {
      this.method.type = METHOD_TYPES.code;
    }
  }
  /**
   * 重置方法，刷新参数配置和代码
   */
  async resetMethod() {
    var _this$method11, _this$method12, _this$method13;
    // 保留旧id methodType
    // unchanged = true
    if (!((_this$method11 = this.method) !== null && _this$method11 !== void 0 && (_this$method11 = _this$method11.meta) !== null && _this$method11 !== void 0 && _this$method11.tpl)) return;
    const {
      type,
      id,
      title
    } = this.method;
    const options = this.getMethodGenerateOptions((_this$method12 = this.method) === null || _this$method12 === void 0 || (_this$method12 = _this$method12.meta) === null || _this$method12 === void 0 ? void 0 : _this$method12.tpl, (_this$method13 = this.method) === null || _this$method13 === void 0 || (_this$method13 = _this$method13.meta) === null || _this$method13 === void 0 ? void 0 : _this$method13.cgi, type);
    const newMethod = await generateMethod(options);
    newMethod.id = id;
    newMethod.title = title;
    this.method = newMethod;
    await this.setValueAndFormat(this.method);
    this.watchMethod(this.method);
    message.success('方法已重置');
  }
}
__decorate([State], MethodEditorState.prototype, "currentTab", void 0);
__decorate([State], MethodEditorState.prototype, "mode", void 0);
__decorate([State], MethodEditorState.prototype, "visible", void 0);
__decorate([State], MethodEditorState.prototype, "sourceId", void 0);
__decorate([Getter], MethodEditorState.prototype, "sourceConfig", null);
__decorate([Getter], MethodEditorState.prototype, "sourceInstance", null);
__decorate([State], MethodEditorState.prototype, "originMethodId", void 0);
__decorate([StateShallow], MethodEditorState.prototype, "dataStub", void 0);
__decorate([State], MethodEditorState.prototype, "generators", void 0);
__decorate([Getter], MethodEditorState.prototype, "methodTemplates", null);
__decorate([Getter], MethodEditorState.prototype, "selectedGenerator", null);
__decorate([State], MethodEditorState.prototype, "selectedTemplateValue", void 0);
__decorate([State], MethodEditorState.prototype, "method", void 0);
__decorate([State], MethodEditorState.prototype, "modified", void 0);
__decorate([State], MethodEditorState.prototype, "hasError", void 0);
__decorate([State], MethodEditorState.prototype, "highlightDataPath", void 0);
__decorate([State], MethodEditorState.prototype, "highlightKeyForDefaultValue", void 0);
__decorate([LazyGetter(function () {
  return getMessageSpy(this);
}), NotEnumerable], MethodEditorState.prototype, "messageSpy", void 0);
export const stateKey = '@wuji_source/MethodEditorState';
export const IMethodEditorState = Inject(stateKey);
//# sourceMappingURL=MethodEditorState.js.map
